/**
 * ThreeCards — the segment's home base.
 *
 * Shows the 3 picked cards, the current round counter, and three actions per card:
 *   - 接受策略 → Timer
 *   - 想调一下这张 → StuckReframe (inline)
 *   - 换一张  → back to "no card focused" so the user can pick another card
 */
import { useMemo, useState } from 'react';
import type { PoolCard } from '../state/cardPool';
import { StuckReframe } from './StuckReframe';
import { playTimerStartCue } from '../timer/audio';

interface ThreeCardsProps {
  wish: string;
  cards: PoolCard[];
  roundNo: number;          // 1..8
  onAccept: (card: PoolCard) => void;
  onUpdateAction: (cardId: string, newAction: string) => void;
  onBackToWish: () => void;
  /** Desk cards (pending or in_progress) NOT yet in this segment — what the user
   * can pull onto the work area to fill an empty slot. */
  availableCards: PoolCard[];
  onAddCard: (card: PoolCard) => void;
}

const SLOT_TOTAL = 3;

/** Cards still in play this segment — completed (done) and torn (dropped) cards
 * are not shown in the work area; they're already archived to the desk drawer. */
function activeForSegment(cards: PoolCard[]): PoolCard[] {
  return cards.filter((c) => c.status === 'pending' || c.status === 'in_progress');
}

export function ThreeCards({
  wish,
  cards,
  roundNo,
  onAccept,
  onUpdateAction,
  onBackToWish,
  availableCards,
  onAddCard,
}: ThreeCardsProps) {
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [stuckCardId, setStuckCardId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const visibleCards = useMemo(() => activeForSegment(cards), [cards]);
  const emptySlots = Math.max(0, SLOT_TOTAL - visibleCards.length);
  const focused = visibleCards.find((c) => c.id === focusedId) ?? null;
  const stuck = visibleCards.find((c) => c.id === stuckCardId) ?? null;

  if (stuck) {
    return (
      <StuckReframe
        card={stuck}
        onAccepted={(newAction) => {
          onUpdateAction(stuck.id, newAction);
          setStuckCardId(null);
          // Re-focus the card so user can press 开始 5 分钟 with the new action
          setFocusedId(stuck.id);
        }}
        onPutAside={() => {
          // User accepted the "放一放" exit: keep card in pool, no action change,
          // close reframe and unfocus so user can pick a different card or end round.
          setStuckCardId(null);
          setFocusedId(null);
        }}
        onCancel={() => setStuckCardId(null)}
      />
    );
  }

  return (
    <section className="panel three-cards">
      <header className="round-strip">
        <span className="tiny-label">
          <i className="dot cyan" /> 第 {roundNo} / 8 轮
        </span>
        <button className="link-subtle" type="button" onClick={onBackToWish}>
          ← 回桌面
        </button>
      </header>
      <h1>工作区里只做一张</h1>
      {wish.trim().length > 0 && <p className="muted">愿望:{wish}</p>}

      <ul className="card-grid three-cards-grid">
        {visibleCards.map((c) => {
          const isFocused = c.id === focusedId;
          return (
            <li key={c.id}>
              <article
                className={`card-checkbox three-card${isFocused ? ' checked' : ''}`}
                onClick={() => setFocusedId(isFocused ? null : c.id)}
              >
                <header>
                  <strong>{c.title}</strong>
                </header>
                <p className="card-row action-row">
                  <span className="row-label cyan">行动</span>
                  <span className="row-text">{c.action}</span>
                </p>
                {c.actionHistory.length > 0 && (
                  <details className="action-history">
                    <summary className="muted tiny">
                      已根据你的反馈调整过 {c.actionHistory.length} 次
                    </summary>
                    <ol>
                      {c.actionHistory.map((s: string, i: number) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ol>
                  </details>
                )}
              </article>
            </li>
          );
        })}
        {Array.from({ length: emptySlots }, (_, i) => (
          <li key={`plus-${i}`}>
            <button
              type="button"
              className="three-card-plus"
              onClick={() => setPickerOpen(true)}
              disabled={availableCards.length === 0}
              aria-label="从桌面加一张便签到工作区"
              title={
                availableCards.length === 0
                  ? '桌面上没有可加入的便签了'
                  : '点击从桌面加一张便签到工作区'
              }
            >
              <PlusGlyph />
              <span className="three-card-plus-label">
                {availableCards.length === 0 ? '桌面上没有了' : '加一张'}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {visibleCards.length === 0 && emptySlots > 0 && availableCards.length === 0 && (
        <p className="muted" style={{ margin: '12px 0 0' }}>
          这一段的卡都已经完成或已放下,桌面上也没有更多便签了。可以回桌面写下新愿望。
        </p>
      )}

      {pickerOpen && (
        <AddCardPicker
          available={availableCards}
          onPick={(card) => {
            onAddCard(card);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {focused && (
        <div className="three-cards-actions">
          <span className="tiny-label">
            <i className="dot lime" /> 「{focused.title}」现在只做这一步
          </span>
          <div className="actions">
            <button
              className="btn-lime"
              type="button"
              onClick={() => {
                playTimerStartCue();
                onAccept(focused);
              }}
            >
              开始 5 分钟
            </button>
            <button
              className="btn-ghost"
              type="button"
              onClick={() => setStuckCardId(focused.id)}
            >
              想调一下这张
            </button>
            <button
              className="link-subtle"
              type="button"
              onClick={() => setFocusedId(null)}
            >
              换一张卡
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function PlusGlyph() {
  // A skeuomorphic + with subtle gradient strokes and a small offset shadow,
  // so the empty slot feels like a "slot waiting for a sticky note" — not a
  // raw plus character.
  return (
    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="three-card-plus-glyph">
      <defs>
        <linearGradient id="plusStroke" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="rgba(28,30,32,.55)" />
          <stop offset="1" stopColor="rgba(28,30,32,.32)" />
        </linearGradient>
      </defs>
      <circle cx="32" cy="32" r="22" fill="none" stroke="rgba(28,30,32,.18)" strokeDasharray="3 4" strokeWidth="1.4" />
      <line x1="32" y1="20" x2="32" y2="44" stroke="url(#plusStroke)" strokeWidth="3.4" strokeLinecap="round" />
      <line x1="20" y1="32" x2="44" y2="32" stroke="url(#plusStroke)" strokeWidth="3.4" strokeLinecap="round" />
    </svg>
  );
}

interface AddCardPickerProps {
  available: PoolCard[];
  onPick: (card: PoolCard) => void;
  onClose: () => void;
}

function AddCardPicker({ available, onPick, onClose }: AddCardPickerProps) {
  return (
    <div className="add-card-picker-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="add-card-picker" onClick={(e) => e.stopPropagation()}>
        <header>
          <span>
            <span className="tiny-label">
              <i className="dot cyan" /> 从桌面挑一张
            </span>
            {available.length > 0 && (
              <span className="muted tiny add-card-picker-count">
                共 {available.length} 张可加入
              </span>
            )}
          </span>
          <button className="link-subtle" type="button" onClick={onClose} aria-label="关闭">
            ×
          </button>
        </header>
        {available.length === 0 ? (
          <p className="muted">桌面上没有更多便签了。</p>
        ) : (
          <ul className="add-card-picker-list">
            {available.map((c) => (
              <li key={c.id}>
                <button type="button" className="add-card-picker-item" onClick={() => onPick(c)}>
                  <strong>{c.title}</strong>
                  <span className="muted tiny">{c.action}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
